/**
 * Service layer for UI Loader.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { Injectable, computed, signal } from '@angular/core';

/**
 * SERVICE: Ui Loader
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class UiLoaderService {
  /** STATE OWNER: _activeCount documents the signal state boundary used by this file. */
  private readonly _activeCount = signal(0);

  /** STATE OWNER: loading documents the computed state boundary used by this file. */
  readonly loading = computed(() => this._activeCount() > 0);

  /**
   * BUSINESS LOGIC: show
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  show(): void {
    this._activeCount.update((n) => n + 1);
  }

  /**
   * BUSINESS LOGIC: hide
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  hide(): void {
    this._activeCount.update((n) => Math.max(0, n - 1));
  }

  async wrap<T>(fn: () => Promise<T>, onError?: (err: unknown) => void): Promise<T | null> {
    this.show();
    try {
      return await fn();
    } catch (err) {
      onError?.(err);
      return null;
    } finally {
      await this.waitForViewToSettle();
      this.hide();
    }
  }

  /**
   * BUSINESS LOGIC: waitForViewToSettle
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private waitForViewToSettle(): Promise<void> {
    if (typeof requestAnimationFrame === 'undefined') {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }
}
